import { useCallback, useEffect, useMemo, useState } from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import moment from 'moment';
import { Button, Input, Text, Textarea } from 'rizzui';
import { toast } from 'sonner';
import CustomSelect from '../../../components/forms/CustomSelect';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import useEducation from '../../../hooks/api-calls/useEducation';
import useProfessionalCategories from '../../../hooks/api-calls/useProfessionalCategories';
import useQuotationGroups from '../../../hooks/api-calls/useQuotationGroups';
import ConfirmationModal from '../../contracts/components/ConfirmationModal';
import SelectorModal from '../../contracts/components/SelectorModal';
import { FacilitatorCallsService } from '../../../services/calls/facilitatorCallsService';
import { ProductService } from '../../../services/product/productService';
import { validateNifNIE } from '../../../utils/validatorFunctions';

type Props = {
    contractId: string;
};

const initialOnlineForm = {
    name: '',
    firstName: '',
    lastName: '',
    documentNumber: '',
    email: '',
    telephone: '',
    birthDate: '',
    studyId: '',
    professionalCategoryId: '',
    quotationGroupId: '',
    productId: '',
};

const initialCourseForm = {
    name: '',
    firstName: '',
    lastName: '',
    documentNumber: '',
    socialSecurityNumber: '',
    email: '',
    telephone: '',
    telephoneSecondary: '',
    mobile: '',
    gender: '',
    birthDate: '',
    studyId: '',
    professionalCategoryId: '',
    quotationGroupId: '',
    functionalArea: '',
    alternativeShippingAddress: '',
    contactSchedule: '',
    disability: false,
    genderViolenceVictim: false,
    terrorismVictim: false,
    remark: '',
    nationality: '',
    postalCode: '',
    city: '',
    province: '',
    country: '',
    productId: '',
};

const tabClassName = 'inline-flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors';
const observationColumnOrder = ['id', 'notes', 'createdByUserName', 'createdAt'];
const extraColumnOrder = ['id', 'productLabel', 'participantName', 'documentNumber', 'email', 'statusLabel', 'createdAt'];

const ensureCurrentOption = (options: any[], value?: string, label?: string) => {
    if (!value) {
        return options;
    }

    if (options.some((option: any) => option.value === value)) {
        return options;
    }

    return [...options, { value, label: label || value }];
};

const normalizeDocumentNumber = (value?: string) => (value || '').replace(/\s+/g, '').toUpperCase();

const isBlank = (value?: string) => !value || value.trim() === '';

const validateExtraForm = (
    form: typeof initialOnlineForm | typeof initialCourseForm
): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (isBlank(form.name)) errors.name = 'El nombre es obligatorio.';
    if (isBlank(form.firstName)) errors.firstName = 'El primer apellido es obligatorio.';
    if (isBlank(form.lastName)) errors.lastName = 'El segundo apellido es obligatorio.';

    if (isBlank(form.documentNumber)) {
        errors.documentNumber = 'El NIF/NIE es obligatorio.';
    } else if (!validateNifNIE(normalizeDocumentNumber(form.documentNumber))) {
        errors.documentNumber = 'El NIF/NIE no es válido.';
    }

    if (isBlank(form.email)) errors.email = 'El correo electrónico es obligatorio.';

    if (isBlank((form as any).telephone)) errors.telephone = 'El teléfono es obligatorio.';

    if (isBlank((form as any).birthDate)) {
        errors.birthDate = 'La fecha de nacimiento es obligatoria.';
    }

    if (isBlank(form.productId)) {
        errors.productId = 'Debes indicar un producto para registrar el extra.';
    }

    return errors;
};

const FacilitatorContractExtras = ({ contractId }: Props) => {
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const service = new FacilitatorCallsService();
    const { getEducationList } = useEducation({ active: true });
    const { getProfessionalCategoriesList } = useProfessionalCategories({ active: true });
    const { getQuotationGroupsList } = useQuotationGroups({ active: true });
    const { filters: tableFilters } = useFilters();
    const [activeTab, setActiveTab] = useState<'observation' | 'online' | 'course'>('observation');
    const [observation, setObservation] = useState('');
    const [onlineForm, setOnlineForm] = useState(initialOnlineForm);
    const [courseForm, setCourseForm] = useState(initialCourseForm);
    const [onlineProductSearch, setOnlineProductSearch] = useState('');
    const [courseProductSearch, setCourseProductSearch] = useState('');
    const [onlineProductOptions, setOnlineProductOptions] = useState<any[]>([]);
    const [courseProductOptions, setCourseProductOptions] = useState<any[]>([]);
    const [selectedOnlineProduct, setSelectedOnlineProduct] = useState<any>(null);
    const [selectedCourseProduct, setSelectedCourseProduct] = useState<any>(null);
    const [lookupSummary, setLookupSummary] = useState<string>('');
    const [isOnlineProductsLoading, setIsOnlineProductsLoading] = useState(false);
    const [isCourseProductsLoading, setIsCourseProductsLoading] = useState(false);
    const [studentSelectorState, setStudentSelectorState] = useState<{
        isOpen: boolean;
        target: 'online' | 'course' | null;
        data: any[];
    }>({
        isOpen: false,
        target: null,
        data: [],
    });
    const [createStudentConfirmation, setCreateStudentConfirmation] = useState<{
        isOpen: boolean;
        type: 'online_course' | 'course' | null;
    }>({
        isOpen: false,
        type: null,
    });
    const [onlineSelectedStudentMetadata, setOnlineSelectedStudentMetadata] = useState<{ studyName?: string; professionalCategoryName?: string; quotationGroupName?: string }>({});
    const [courseSelectedStudentMetadata, setCourseSelectedStudentMetadata] = useState<{ studyName?: string; professionalCategoryName?: string; quotationGroupName?: string }>({});
    const [onlineErrors, setOnlineErrors] = useState<Record<string, string>>({});
    const [courseErrors, setCourseErrors] = useState<Record<string, string>>({});

    const [response, loading, error, refetch] = useFetch(useCallback(async () => {
        const apiResponse = await service.getContractExtras(contractId);
        return apiResponse.getResponseData();
    }, [contractId]));
    const canMarkExtraDone = userCan('show_contract_extras', 'contracts') || userCan('show_all_pending_contract_extras', 'contracts');

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (onlineProductSearch.trim().length < 3) {
                setOnlineProductOptions([]);
                return;
            }

            try {
                setIsOnlineProductsLoading(true);
                const apiResponse = await (await new ProductService().listProductsForSelect({
                    limit: 20,
                    filter_filters: { search_text: onlineProductSearch },
                })).getResponseData();
                setOnlineProductOptions(
                    (apiResponse?.data ?? []).map((option: any) => ({
                        value: option.id,
                        label: `${option.code ? `${option.code} - ` : ''}${option.name}`,
                        code: option.code,
                        name: option.name,
                    }))
                );
            } catch (_error) {
                setOnlineProductOptions([]);
            } finally {
                setIsOnlineProductsLoading(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [onlineProductSearch]);

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (courseProductSearch.trim().length < 3) {
                setCourseProductOptions([]);
                return;
            }

            try {
                setIsCourseProductsLoading(true);
                const apiResponse = await (await new ProductService().listProductsForSelect({
                    limit: 20,
                    filter_filters: { search_text: courseProductSearch },
                })).getResponseData();
                setCourseProductOptions(
                    (apiResponse?.data ?? []).map((option: any) => ({
                        value: option.id,
                        label: `${option.code ? `${option.code} - ` : ''}${option.name}`,
                        code: option.code,
                        name: option.name,
                    }))
                );
            } catch (_error) {
                setCourseProductOptions([]);
            } finally {
                setIsCourseProductsLoading(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [courseProductSearch]);

    const onlineProductLabel = useMemo(() => {
        const option = onlineProductOptions.find((item: any) => item.value === onlineForm.productId) ?? selectedOnlineProduct;
        return option?.label ?? '';
    }, [onlineForm.productId, onlineProductOptions, selectedOnlineProduct]);

    const courseProductLabel = useMemo(() => {
        const option = courseProductOptions.find((item: any) => item.value === courseForm.productId) ?? selectedCourseProduct;
        return option?.label ?? '';
    }, [courseForm.productId, courseProductOptions, selectedCourseProduct]);

    const onlineEducationOptions = useMemo(
        () => ensureCurrentOption(getEducationList(), onlineForm.studyId, onlineSelectedStudentMetadata.studyName),
        [getEducationList, onlineForm.studyId, onlineSelectedStudentMetadata.studyName]
    );
    const onlineProfessionalCategoryOptions = useMemo(
        () => ensureCurrentOption(getProfessionalCategoriesList(), onlineForm.professionalCategoryId, onlineSelectedStudentMetadata.professionalCategoryName),
        [getProfessionalCategoriesList, onlineForm.professionalCategoryId, onlineSelectedStudentMetadata.professionalCategoryName]
    );
    const onlineQuotationGroupOptions = useMemo(
        () => ensureCurrentOption(getQuotationGroupsList(), onlineForm.quotationGroupId, onlineSelectedStudentMetadata.quotationGroupName),
        [getQuotationGroupsList, onlineForm.quotationGroupId, onlineSelectedStudentMetadata.quotationGroupName]
    );
    const courseEducationOptions = useMemo(
        () => ensureCurrentOption(getEducationList(), courseForm.studyId, courseSelectedStudentMetadata.studyName),
        [getEducationList, courseForm.studyId, courseSelectedStudentMetadata.studyName]
    );
    const courseProfessionalCategoryOptions = useMemo(
        () => ensureCurrentOption(getProfessionalCategoriesList(), courseForm.professionalCategoryId, courseSelectedStudentMetadata.professionalCategoryName),
        [getProfessionalCategoriesList, courseForm.professionalCategoryId, courseSelectedStudentMetadata.professionalCategoryName]
    );
    const courseQuotationGroupOptions = useMemo(
        () => ensureCurrentOption(getQuotationGroupsList(), courseForm.quotationGroupId, courseSelectedStudentMetadata.quotationGroupName),
        [getQuotationGroupsList, courseForm.quotationGroupId, courseSelectedStudentMetadata.quotationGroupName]
    );

    const fillWithStudent = (student: any, target: 'online' | 'course') => {
        if (target === 'online') {
            setOnlineSelectedStudentMetadata({
                studyName: student?.studyName || '',
                professionalCategoryName: student?.professionalCategoryName || '',
                quotationGroupName: student?.quotationGroupName || '',
            });
            setOnlineForm((current) => ({
                ...current,
                name: student?.name || current.name,
                firstName: student?.firstName || current.firstName,
                lastName: student?.lastName || current.lastName,
                documentNumber: student?.nif || current.documentNumber,
                email: student?.email || current.email,
                telephone: student?.telephone || current.telephone,
                birthDate: student?.birthDate || current.birthDate,
                studyId: student?.studyId ?? '',
                professionalCategoryId: student?.professionalCategoryId ?? '',
                quotationGroupId: student?.quotationGroupId ?? '',
            }));
            return;
        }

        setCourseSelectedStudentMetadata({
            studyName: student?.studyName || '',
            professionalCategoryName: student?.professionalCategoryName || '',
            quotationGroupName: student?.quotationGroupName || '',
        });
        setCourseForm((current) => ({
            ...current,
            name: student?.name || current.name,
            firstName: student?.firstName || current.firstName,
            lastName: student?.lastName || current.lastName,
            documentNumber: student?.nif || current.documentNumber,
            socialSecurityNumber: student?.socialSecurityNumber || current.socialSecurityNumber,
            email: student?.email || current.email,
            telephone: student?.telephone || current.telephone,
            telephoneSecondary: student?.telephoneSecondary || current.telephoneSecondary,
            mobile: student?.mobile || current.mobile,
            gender: student?.gender || current.gender,
            birthDate: student?.birthDate || current.birthDate,
            studyId: student?.studyId ?? '',
            professionalCategoryId: student?.professionalCategoryId ?? '',
            quotationGroupId: student?.quotationGroupId ?? '',
            functionalArea: student?.functionalArea || current.functionalArea,
            alternativeShippingAddress: student?.alternativeShippingAddress || current.alternativeShippingAddress,
            contactSchedule: student?.contactSchedule || current.contactSchedule,
            disability: student?.disability ?? current.disability,
            genderViolenceVictim: student?.genderViolenceVictim ?? current.genderViolenceVictim,
            terrorismVictim: student?.terrorismVictim ?? current.terrorismVictim,
            remark: student?.remark || current.remark,
        }));
    };

    const handleLookup = async (target: 'online' | 'course') => {
        const source = target === 'online' ? onlineForm : courseForm;
        if (!source.documentNumber && !source.email) {
            return;
        }

        try {
            const apiResponse = await (await service.lookupExtraStudent({
                contractId,
                documentNumber: source.documentNumber,
                email: source.email,
                socialSecurityNumber: (source as any).socialSecurityNumber,
                telephone: (source as any).telephone,
                telephoneSecondary: (source as any).telephoneSecondary,
                mobile: (source as any).mobile,
            })).getResponseData();

            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            if (apiResponse.data?.student) {
                setStudentSelectorState({
                    isOpen: true,
                    target,
                    data: [apiResponse.data.student],
                });
                setLookupSummary('Alumno existente localizado en el sistema.');
            } else {
                setLookupSummary('No existe coincidencia automática para los identificadores indicados.');
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const updateOnlineForm = (changes: Partial<typeof initialOnlineForm>) => {
        setOnlineForm((current) => {
            const next = { ...current, ...changes };
            if (Object.keys(onlineErrors).length > 0) {
                setOnlineErrors(validateExtraForm(next));
            }
            return next;
        });
    };

    const updateCourseForm = (changes: Partial<typeof initialCourseForm>) => {
        setCourseForm((current) => {
            const next = { ...current, ...changes };
            if (Object.keys(courseErrors).length > 0) {
                setCourseErrors(validateExtraForm(next));
            }
            return next;
        });
    };

    const handleSaveObservation = async () => {
        const trimmedObservation = observation.trim();

        if (!trimmedObservation) {
            toast.error('Debes introducir una observación antes de guardarla');
            return;
        }

        try {
            const apiResponse = await (await service.saveContractExtraObservation({ contractId, notes: trimmedObservation })).getResponseData();
            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            toast.success('Observaciones guardadas');
            setObservation('');
            refetch();
        } catch (error) {
            handleErrors(error);
        }
    };

    const submitExtra = async (type: 'online_course' | 'course', forceCreateStudent = false) => {
        const source = type === 'online_course' ? onlineForm : courseForm;
        const errors = validateExtraForm(source);

        if (type === 'online_course') {
            setOnlineErrors(errors);
        } else {
            setCourseErrors(errors);
        }

        if (Object.keys(errors).length > 0) {
            toast.error('Revisa los campos obligatorios del formulario');
            return;
        }

        try {
            const apiResponse = await (await service.createContractExtra({
                contractId,
                type,
                ...source,
                documentNumber: normalizeDocumentNumber(source.documentNumber),
                createStudentIfMissing: forceCreateStudent,
                payload: type === 'course'
                    ? {
                        nationality: (source as any).nationality,
                        postalCode: (source as any).postalCode,
                        city: (source as any).city,
                        province: (source as any).province,
                        country: (source as any).country,
                    }
                    : {},
            })).getResponseData();

            if (!apiResponse?.success) {
                if (apiResponse?.data?.requiresStudentCreationConfirmation) {
                    setCreateStudentConfirmation({
                        isOpen: true,
                        type,
                    });
                    return;
                }

                handleErrors(apiResponse);
                return;
            }

            toast.success('Extra registrado correctamente');
            setLookupSummary('');
            if (type === 'online_course') {
                setOnlineForm(initialOnlineForm);
                setOnlineErrors({});
                setOnlineProductSearch('');
                setSelectedOnlineProduct(null);
                setOnlineSelectedStudentMetadata({});
            } else {
                setCourseForm(initialCourseForm);
                setCourseErrors({});
                setCourseProductSearch('');
                setSelectedCourseProduct(null);
                setCourseSelectedStudentMetadata({});
            }
            refetch();
        } catch (error) {
            handleErrors(error);
        }
    };

    const handleMarkDone = async (extraId: string) => {
        try {
            const apiResponse = await (await service.markContractExtraDone(extraId)).getResponseData();
            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            toast.success('Extra marcado como hecho');
            refetch();
        } catch (error) {
            handleErrors(error);
        }
    };

    const extrasResponse = response ?? {};
    const observationRows = useMemo(() => {
        const rows = Array.isArray(extrasResponse.observations) ? extrasResponse.observations : [];

        return rows.map((row: any) => ({
            id: row.id,
            notes: row.payload?.notes || 'Sin observaciones',
            createdByUserName: [row.createdByUser?.name, row.createdByUser?.lastName].filter(Boolean).join(' ') || 'N/A',
            createdAt: row.createdAt,
        }));
    }, [extrasResponse.observations]);

    const onlineCourseRows = useMemo(() => {
        const rows = Array.isArray(extrasResponse.onlineCourses) ? extrasResponse.onlineCourses : [];

        return rows.map((row: any) => ({
            id: row.id,
            productLabel: row.product ? `${row.product.code ? `${row.product.code} - ` : ''}${row.product.name}` : (row.title || 'N/A'),
            participantName: [row.name, row.firstName, row.lastName].filter(Boolean).join(' ') || 'N/A',
            documentNumber: row.documentNumber || 'N/A',
            email: row.email || 'N/A',
            rawStatus: row.status,
            statusLabel: row.status === 'DONE' ? 'Hecho' : 'Pendiente',
            createdAt: row.createdAt,
        }));
    }, [extrasResponse.onlineCourses]);

    const courseRows = useMemo(() => {
        const rows = Array.isArray(extrasResponse.courses) ? extrasResponse.courses : [];

        return rows.map((row: any) => ({
            id: row.id,
            productLabel: row.product ? `${row.product.code ? `${row.product.code} - ` : ''}${row.product.name}` : (row.title || 'N/A'),
            participantName: [row.name, row.firstName, row.lastName].filter(Boolean).join(' ') || 'N/A',
            documentNumber: row.documentNumber || 'N/A',
            email: row.email || 'N/A',
            rawStatus: row.status,
            statusLabel: row.status === 'DONE' ? 'Hecho' : 'Pendiente',
            createdAt: row.createdAt,
        }));
    }, [extrasResponse.courses]);

    if (loading && !response) {
        return <Loader height="18vh" />;
    }

    if (!loading && error) {
        return (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {'No se ha podido cargar la sección de extras para este contrato.'}
            </div>
        );
    }

    return (
        <>
            <div className="space-y-4">
                <div className="pt-2">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Gestión de extras
                    </div>
                    <div className="inline-flex flex-wrap gap-1 rounded-md border border-gray-200 bg-gray-50 p-1">
                        {[
                            ['observation', 'Observaciones'],
                            ['online', 'Cursos online extras'],
                            ['course', 'Cursos extra'],
                        ].map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setActiveTab(key as 'observation' | 'online' | 'course')}
                                className={`${tabClassName} ${activeTab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-900'}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {lookupSummary && (
                    <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                        {lookupSummary}
                    </div>
                )}

                {activeTab === 'observation' && (
                    <div className="space-y-4">
                        <div className="rounded-md border border-gray-200 bg-white p-4">
                            <Textarea
                                value={observation}
                                onChange={(event) => setObservation(event.target.value)}
                                rows={6}
                                placeholder="Observaciones adicionales del contrato"
                            />
                            <div className="mt-4 flex justify-end">
                                <Button disabled={!observation.trim()} onClick={handleSaveObservation}>Guardar observaciones</Button>
                            </div>
                        </div>

                        <div className="rounded-md border border-gray-200 bg-white p-4">
                            <Text className="mb-4 text-base font-semibold text-gray-900">Histórico de observaciones</Text>
                            {observationRows.length === 0 ? (
                                <Text className="text-sm text-gray-500">Sin observaciones registradas.</Text>
                            ) : (
                                <CustomTable
                                    id="contract-extras-observations-table"
                                    data={observationRows}
                                    columnOrder={observationColumnOrder}
                                    columnsNotShown={['id']}
                                    filters={tableFilters}
                                    search={false}
                                    filter={false}
                                    pagination={false}
                                    isSortable={false}
                                    overrideColumns={[
                                        {
                                            key: 'notes',
                                            columnNameKey: 'Observación',
                                            render: (row: any) => row.row.original.notes,
                                        },
                                        {
                                            key: 'createdByUserName',
                                            columnNameKey: 'Registrado por',
                                            render: (row: any) => row.row.original.createdByUserName,
                                        },
                                        {
                                            key: 'createdAt',
                                            columnNameKey: 'Fecha',
                                            render: (row: any) => row.row.original.createdAt?.date ? moment(row.row.original.createdAt.date).format('DD/MM/YYYY HH:mm') : 'N/A',
                                        },
                                    ]}
                                />
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'online' && (
                    <div className="space-y-4">
                        <div className="rounded-md border border-gray-200 bg-white p-4">
                            <div className="grid gap-4 md:grid-cols-2">
                                <Input label="Nombre" required value={onlineForm.name} onChange={(event) => updateOnlineForm({ name: event.target.value })} placeholder="Nombre" error={onlineErrors.name} />
                                <Input label="Primer apellido" required value={onlineForm.firstName} onChange={(event) => updateOnlineForm({ firstName: event.target.value })} placeholder="Primer apellido" error={onlineErrors.firstName} />
                                <Input label="Segundo apellido" required value={onlineForm.lastName} onChange={(event) => updateOnlineForm({ lastName: event.target.value })} placeholder="Segundo apellido" error={onlineErrors.lastName} />
                                <Input
                                    label="NIF/NIE"
                                    required
                                    value={onlineForm.documentNumber}
                                    onChange={(event) => updateOnlineForm({ documentNumber: event.target.value.toUpperCase() })}
                                    onBlur={() => handleLookup('online')}
                                    placeholder="NIF/NIE"
                                    error={onlineErrors.documentNumber}
                                />
                                <Input
                                    label="Correo electrónico"
                                    required
                                    value={onlineForm.email}
                                    onChange={(event) => updateOnlineForm({ email: event.target.value })}
                                    placeholder="Correo electrónico"
                                    error={onlineErrors.email}
                                />
                                <Input
                                    label="Teléfono"
                                    required
                                    value={onlineForm.telephone}
                                    onChange={(event) => updateOnlineForm({ telephone: event.target.value })}
                                    placeholder="Teléfono"
                                    error={onlineErrors.telephone}
                                />
                                <Input
                                    label="Fecha de nacimiento"
                                    required
                                    value={onlineForm.birthDate}
                                    type="date"
                                    onChange={(event) => updateOnlineForm({ birthDate: event.target.value })}
                                    placeholder="Fecha de nacimiento"
                                    error={onlineErrors.birthDate}
                                />
                                <div className="space-y-2">
                                    <CustomSelect
                                        isSearchable
                                        isClearable
                                        id="onlineStudyId"
                                        label="Estudios"
                                        isDisabled={!userCan('get_studies_for_select', 'studies')}
                                        value={onlineForm.studyId ? { value: onlineForm.studyId, label: onlineEducationOptions.find((g: any) => g.value === onlineForm.studyId)?.label } : null}
                                        options={onlineEducationOptions}
                                        onChange={(e: any) => { updateOnlineForm({ studyId: e?.value ?? '' }); }}
                                    />
                                    {!userCan('get_studies_for_select', 'studies') && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                            <span>No tienes permisos para ver los estudios</span>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <CustomSelect
                                        isSearchable
                                        isClearable
                                        id="onlineProfessionalCategoryId"
                                        label="Categoria Profesional"
                                        isDisabled={!userCan('get_professional_categories_for_select', 'professional_categories')}
                                        value={onlineForm.professionalCategoryId ? { value: onlineForm.professionalCategoryId, label: onlineProfessionalCategoryOptions.find((g: any) => g.value === onlineForm.professionalCategoryId)?.label } : null}
                                        options={onlineProfessionalCategoryOptions}
                                        onChange={(e: any) => { updateOnlineForm({ professionalCategoryId: e?.value ?? '' }); }}
                                    />
                                    {!userCan('get_professional_categories_for_select', 'professional_categories') && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                            <span>No tienes permisos para ver las categorías profesionales</span>
                                        </div>
                                    )}
                                </div>
                                <CustomSelect
                                    isSearchable
                                    isClearable
                                    id="onlineQuotationGroupId"
                                    label="Grupo de Cotización"
                                    value={onlineForm.quotationGroupId ? { value: onlineForm.quotationGroupId, label: onlineQuotationGroupOptions.find((g: any) => g.value === onlineForm.quotationGroupId)?.label } : null}
                                    options={onlineQuotationGroupOptions}
                                    onChange={(e: any) => { updateOnlineForm({ quotationGroupId: e?.value ?? '' }); }}
                                />
                                <CustomSelect
                                    id="onlineExtraProductId"
                                    label="Producto"
                                    required
                                    containerClassName="md:col-span-2"
                                    error={onlineErrors.productId}
                                    isSearchable
                                    isClearable
                                    options={onlineProductOptions}
                                    value={onlineForm.productId ? onlineProductOptions.find((item: any) => item.value === onlineForm.productId) ?? selectedOnlineProduct ?? { value: onlineForm.productId, label: onlineProductLabel } : null}
                                    onChange={(option: any) => {
                                        updateOnlineForm({ productId: option?.value ?? '' });
                                        setSelectedOnlineProduct(option ?? null);
                                        setOnlineProductSearch('');
                                    }}
                                    onInputChange={(value: string, meta: any) => {
                                        if (meta.action === 'input-change') {
                                            setOnlineProductSearch(value);
                                        }
                                    }}
                                    inputValue={onlineProductSearch}
                                    isLoading={isOnlineProductsLoading}
                                    placeholder="producto"
                                    noOption={() => onlineProductSearch.trim().length < 3 ? 'Escribe al menos 3 caracteres' : 'No se ha encontrado ningún resultado'}
                                />
                            </div>

                            <div className="mt-4 flex justify-end">
                                <Button onClick={() => submitExtra('online_course')}>Registrar curso online extra</Button>
                            </div>
                        </div>

                        <div className="rounded-md border border-gray-200 bg-white p-4">
                            <Text className="mb-4 text-base font-semibold text-gray-900">Cursos online extra registrados</Text>
                            {onlineCourseRows.length === 0 ? (
                                <Text className="text-sm text-gray-500">Sin cursos online extra registrados.</Text>
                            ) : (
                                <CustomTable
                                    id="contract-extras-online-table"
                                    data={onlineCourseRows}
                                    columnOrder={extraColumnOrder}
                                    columnsNotShown={['id', 'rawStatus']}
                                    filters={tableFilters}
                                    search={false}
                                    filter={false}
                                    pagination={false}
                                    isSortable={false}
                                    overrideColumns={[
                                        { key: 'productLabel', columnNameKey: 'Producto', render: (row: any) => row.row.original.productLabel },
                                        { key: 'participantName', columnNameKey: 'Alumno', render: (row: any) => row.row.original.participantName },
                                        { key: 'documentNumber', columnNameKey: 'NIF/NIE', render: (row: any) => row.row.original.documentNumber },
                                        { key: 'email', columnNameKey: 'Email', render: (row: any) => row.row.original.email },
                                        { key: 'statusLabel', columnNameKey: 'Estado', render: (row: any) => row.row.original.statusLabel },
                                        {
                                            key: 'createdAt',
                                            columnNameKey: 'Fecha',
                                            render: (row: any) => row.row.original.createdAt?.date ? moment(row.row.original.createdAt.date).format('DD/MM/YYYY HH:mm') : 'N/A',
                                        },
                                    ]}
                                    actions={canMarkExtraDone ? [
                                        {
                                            label: 'Done',
                                            render: (row: any) => row.rawStatus === 'DONE' ? null : (
                                                <Button size="sm" onClick={() => handleMarkDone(row.id)}>
                                                    Hecho
                                                </Button>
                                            ),
                                        },
                                    ] : []}
                                />
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'course' && (
                    <div className="space-y-4">
                        <div className="rounded-md border border-gray-200 bg-white p-4">
                            {!extrasResponse.canCreateCourse && (
                                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                    Esta pestaña solo admite altas cuando la fecha de fin del contrato ya se ha cumplido.
                                </div>
                            )}

                            <div className="grid gap-4 md:grid-cols-3">
                                <Input label="Nombre" required value={courseForm.name} onChange={(event) => updateCourseForm({ name: event.target.value })} placeholder="Nombre" error={courseErrors.name} />
                                <Input label="Primer apellido" required value={courseForm.firstName} onChange={(event) => updateCourseForm({ firstName: event.target.value })} placeholder="Primer apellido" error={courseErrors.firstName} />
                                <Input label="Segundo apellido" required value={courseForm.lastName} onChange={(event) => updateCourseForm({ lastName: event.target.value })} placeholder="Segundo apellido" error={courseErrors.lastName} />
                                <Input label="NIF/NIE" required value={courseForm.documentNumber} onChange={(event) => updateCourseForm({ documentNumber: event.target.value.toUpperCase() })} onBlur={() => handleLookup('course')} placeholder="NIF/NIE" error={courseErrors.documentNumber} />
                                <Input value={courseForm.socialSecurityNumber} onChange={(event) => updateCourseForm({ socialSecurityNumber: event.target.value })} placeholder="NSS" />
                                <Input label="Fecha de nacimiento" required value={courseForm.birthDate} type="date" onChange={(event) => updateCourseForm({ birthDate: event.target.value })} placeholder="Fecha de nacimiento" error={courseErrors.birthDate} />
                                <Input label="Correo electrónico" required value={courseForm.email} onChange={(event) => updateCourseForm({ email: event.target.value })} placeholder="Email" error={courseErrors.email} />
                                <Input label="Teléfono" required value={courseForm.telephone} onChange={(event) => updateCourseForm({ telephone: event.target.value })} placeholder="Teléfono" error={courseErrors.telephone} />
                                <Input value={courseForm.telephoneSecondary} onChange={(event) => updateCourseForm({ telephoneSecondary: event.target.value })} placeholder="Teléfono secundario" />
                                <Input value={courseForm.mobile} onChange={(event) => updateCourseForm({ mobile: event.target.value })} placeholder="Móvil" />
                                <select className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={courseForm.gender} onChange={(event) => setCourseForm((current) => ({ ...current, gender: event.target.value }))}>
                                    <option value="">Sexo</option>
                                    <option value="male">Masculino</option>
                                    <option value="female">Femenino</option>
                                </select>
                                <div className="space-y-2">
                                    <CustomSelect
                                        isSearchable
                                        isClearable
                                        id="courseStudyId"
                                        label="Estudios"
                                        isDisabled={!userCan('get_studies_for_select', 'studies')}
                                        value={courseForm.studyId ? { value: courseForm.studyId, label: courseEducationOptions.find((g: any) => g.value === courseForm.studyId)?.label } : null}
                                        options={courseEducationOptions}
                                        onChange={(e: any) => { updateCourseForm({ studyId: e?.value ?? '' }); }}
                                    />
                                    {!userCan('get_studies_for_select', 'studies') && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                            <span>No tienes permisos para ver los estudios</span>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <CustomSelect
                                        isSearchable
                                        isClearable
                                        id="courseProfessionalCategoryId"
                                        label="Categoria Profesional"
                                        isDisabled={!userCan('get_professional_categories_for_select', 'professional_categories')}
                                        value={courseForm.professionalCategoryId ? { value: courseForm.professionalCategoryId, label: courseProfessionalCategoryOptions.find((g: any) => g.value === courseForm.professionalCategoryId)?.label } : null}
                                        options={courseProfessionalCategoryOptions}
                                        onChange={(e: any) => { updateCourseForm({ professionalCategoryId: e?.value ?? '' }); }}
                                    />
                                    {!userCan('get_professional_categories_for_select', 'professional_categories') && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                            <span>No tienes permisos para ver las categorías profesionales</span>
                                        </div>
                                    )}
                                </div>
                                <CustomSelect
                                    isSearchable
                                    isClearable
                                    id="courseQuotationGroupId"
                                    label="Grupo de Cotización"
                                    value={courseForm.quotationGroupId ? { value: courseForm.quotationGroupId, label: courseQuotationGroupOptions.find((g: any) => g.value === courseForm.quotationGroupId)?.label } : null}
                                    options={courseQuotationGroupOptions}
                                    onChange={(e: any) => { updateCourseForm({ quotationGroupId: e?.value ?? '' }); }}
                                />
                                <Input value={courseForm.functionalArea} onChange={(event) => updateCourseForm({ functionalArea: event.target.value })} placeholder="Área funcional" />
                                <Input value={courseForm.contactSchedule} onChange={(event) => updateCourseForm({ contactSchedule: event.target.value })} placeholder="Horario de contacto" />
                                <Input value={courseForm.alternativeShippingAddress} onChange={(event) => updateCourseForm({ alternativeShippingAddress: event.target.value })} placeholder="Dirección alternativa" />
                                <Input value={courseForm.nationality} onChange={(event) => updateCourseForm({ nationality: event.target.value })} placeholder="Nacionalidad" />
                                <Input value={courseForm.postalCode} onChange={(event) => updateCourseForm({ postalCode: event.target.value })} placeholder="Código postal" />
                                <Input value={courseForm.city} onChange={(event) => updateCourseForm({ city: event.target.value })} placeholder="Ciudad" />
                                <Input value={courseForm.province} onChange={(event) => updateCourseForm({ province: event.target.value })} placeholder="Provincia" />
                                <Input value={courseForm.country} onChange={(event) => updateCourseForm({ country: event.target.value })} placeholder="País" />
                                <CustomSelect
                                    id="courseExtraProductId"
                                    label="Producto"
                                    required
                                    containerClassName="md:col-span-3"
                                    error={courseErrors.productId}
                                    isSearchable
                                    isClearable
                                    options={courseProductOptions}
                                    value={courseForm.productId ? courseProductOptions.find((item: any) => item.value === courseForm.productId) ?? selectedCourseProduct ?? { value: courseForm.productId, label: courseProductLabel } : null}
                                    onChange={(option: any) => {
                                        updateCourseForm({ productId: option?.value ?? '' });
                                        setSelectedCourseProduct(option ?? null);
                                        setCourseProductSearch('');
                                    }}
                                    onInputChange={(value: string, meta: any) => {
                                        if (meta.action === 'input-change') {
                                            setCourseProductSearch(value);
                                        }
                                    }}
                                    inputValue={courseProductSearch}
                                    isLoading={isCourseProductsLoading}
                                    placeholder="producto"
                                    noOption={() => courseProductSearch.trim().length < 3 ? 'Escribe al menos 3 caracteres' : 'No se ha encontrado ningún resultado'}
                                />
                                <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={courseForm.disability} onChange={(event) => setCourseForm((current) => ({ ...current, disability: event.target.checked }))} /> Discapacidad</label>
                                <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={courseForm.genderViolenceVictim} onChange={(event) => updateCourseForm({ genderViolenceVictim: event.target.checked })} /> Víctima violencia de género</label>
                                <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={courseForm.terrorismVictim} onChange={(event) => updateCourseForm({ terrorismVictim: event.target.checked })} /> Víctima terrorismo</label>
                                <div className="md:col-span-3">
                                    <Textarea value={courseForm.remark} onChange={(event) => updateCourseForm({ remark: event.target.value })} rows={4} placeholder="Observaciones del curso extra" />
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                                <Button disabled={!extrasResponse.canCreateCourse} onClick={() => submitExtra('course')}>
                                    Registrar curso extra
                                </Button>
                            </div>
                        </div>

                        <div className="rounded-md border border-gray-200 bg-white p-4">
                            <Text className="mb-4 text-base font-semibold text-gray-900">Cursos extra registrados</Text>
                            {courseRows.length === 0 ? (
                                <Text className="text-sm text-gray-500">Sin cursos extra registrados.</Text>
                            ) : (
                                <CustomTable
                                    id="contract-extras-course-table"
                                    data={courseRows}
                                    columnOrder={extraColumnOrder}
                                    columnsNotShown={['id', 'rawStatus']}
                                    filters={tableFilters}
                                    search={false}
                                    filter={false}
                                    pagination={false}
                                    isSortable={false}
                                    overrideColumns={[
                                        { key: 'productLabel', columnNameKey: 'Producto', render: (row: any) => row.row.original.productLabel },
                                        { key: 'participantName', columnNameKey: 'Alumno', render: (row: any) => row.row.original.participantName },
                                        { key: 'documentNumber', columnNameKey: 'NIF/NIE', render: (row: any) => row.row.original.documentNumber },
                                        { key: 'email', columnNameKey: 'Email', render: (row: any) => row.row.original.email },
                                        { key: 'statusLabel', columnNameKey: 'Estado', render: (row: any) => row.row.original.statusLabel },
                                        {
                                            key: 'createdAt',
                                            columnNameKey: 'Fecha',
                                            render: (row: any) => row.row.original.createdAt?.date ? moment(row.row.original.createdAt.date).format('DD/MM/YYYY HH:mm') : 'N/A',
                                        },
                                    ]}
                                    actions={canMarkExtraDone ? [
                                        {
                                            label: 'Done',
                                            render: (row: any) => row.rawStatus === 'DONE' ? null : (
                                                <Button size="sm" onClick={() => handleMarkDone(row.id)}>
                                                    Hecho
                                                </Button>
                                            ),
                                        },
                                    ] : []}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            <SelectorModal
                isOpen={studentSelectorState.isOpen}
                onClose={() => setStudentSelectorState({ isOpen: false, target: null, data: [] })}
                data={studentSelectorState.data}
                title="Alumno/empleado existente encontrado"
                selectedLabel="Seleccionado"
                buttonText="Usar este alumno"
                renderItem={(student: any) => (
                    <Text>
                        <span className="font-medium text-gray-900">
                            {[student?.name, student?.firstName, student?.lastName].filter(Boolean).join(' ')}
                        </span>
                        <span className="text-sm text-gray-600">
                            {' '}
                            {[student?.nif, student?.email].filter(Boolean).join(' · ')}
                        </span>
                    </Text>
                )}
                onSubmit={(student: any) => {
                    if (student && studentSelectorState.target) {
                        fillWithStudent(student, studentSelectorState.target);
                    }
                    setStudentSelectorState({ isOpen: false, target: null, data: [] });
                }}
            />

            <ConfirmationModal
                isOpen={createStudentConfirmation.isOpen}
                onClose={() => setCreateStudentConfirmation({ isOpen: false, type: null })}
                onConfirm={() => {
                    const type = createStudentConfirmation.type;
                    setCreateStudentConfirmation({ isOpen: false, type: null });
                    if (type) {
                        submitExtra(type, true);
                    }
                }}
                title="Crear alumno automáticamente"
                message="No existe un alumno coincidente. Si continúas, se creará automáticamente con los datos introducidos y se registrará el extra."
                confirmText="Crear y continuar"
                cancelText="Cancelar"
            />
        </>
    );
};

export default FacilitatorContractExtras;
